/**
 * Redirect-loop diagnostics. Staging only (gated in diag()).
 *
 * The login loop is a sequence of full page redirects, so console output is wiped
 * roughly every eleven seconds and has never survived long enough to be read. This
 * keeps a ring buffer in localStorage instead, which outlives both the redirect and
 * the tab, and can be read back afterwards with __loopDiag() in the console.
 *
 * Every entry point is wrapped so a diagnostic can never be the reason the app fails.
 */
import { environment } from 'src/environments/environment';

const KEY = 'diag.loop';
/** Bumped per staging deploy so entries from different builds can be told apart. */
export const DIAG_BUILD = 'msal-shape-2';
const MAX_ENTRIES = 1200;
// Hard byte ceiling. This buffer shares localStorage with MSAL's own token cache, and
// filling the quota would make MSAL fail to persist an account — which is one of the
// things being investigated. The instrumentation must not be able to cause the fault.
const MAX_BYTES = 1_000_000;

export type DiagEntry = {
    t: string;
    label: string;
    url: string;
    [key: string]: unknown;
};

function read(): DiagEntry[] {
    try {
        const raw = localStorage.getItem(KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export function diag(label: string, data?: Record<string, unknown>): void {
    // Staging only. The buffer is read back by hand during investigation and has no
    // business persisting on client machines in production.
    if (!environment.isStaging)
        return;

    try {
        const buffer = read();
        buffer.push({
            t: new Date().toISOString(),
            label,
            url: location.pathname + location.search + location.hash.slice(0, 60),
            ...data
        });

        while (buffer.length > MAX_ENTRIES)
            buffer.shift();

        let serialized = JSON.stringify(buffer);
        while (serialized.length > MAX_BYTES && buffer.length > 1) {
            buffer.splice(0, Math.ceil(buffer.length / 4));
            serialized = JSON.stringify(buffer);
        }

        localStorage.setItem(KEY, serialized);
    } catch {
        // Never let instrumentation break the thing it is instrumenting.
    }
}

/**
 * The state that decides whether the app redirects to Microsoft again. If the loop is
 * a lost account, accounts is 0 here on every pass while the sign-in itself succeeds.
 */
export function diagMsal(label: string, instance: unknown, extra?: Record<string, unknown>): void {
    try {
        const msal = instance as {
            getAllAccounts?: () => Array<{ homeAccountId?: string, username?: string }>,
            getActiveAccount?: () => { homeAccountId?: string, username?: string } | null
        };

        const accounts = msal?.getAllAccounts?.() ?? [];
        const active = msal?.getActiveAccount?.() ?? null;

        diag(label, {
            accounts: accounts.length,
            usernames: accounts.map(a => a?.username ?? '?'),
            active: active?.username ?? null,
            hasCodeInHash: location.hash.includes('code='),
            // Whether msal-browser v5 can still read its encrypted cache back.
            encryptionCookie: document.cookie.includes('msal.cache.encryption'),
            msalKeys: Object.keys(localStorage).filter(k => k.startsWith('msal.') || k.includes('-login.windows.net-')).length,
            ...extra
        });
    } catch {
        // As above.
    }
}

export function installLoopDiagnostics(): void {
    try {
        (window as unknown as Record<string, unknown>)['__loopDiag'] = () => read();
        (window as unknown as Record<string, unknown>)['__loopDiagClear'] = () => localStorage.removeItem(KEY);
        diag('page-load', { build: DIAG_BUILD, ref: document.referrer.slice(0, 80), nav: performance?.getEntriesByType?.('navigation')?.[0]?.['entryType'] ?? '?' });
    } catch {
        // As above.
    }
}
